/**
 * deviceCoupling.js -- The rules of device-to-device coupling, in one place.
 *
 * A coupling is a physical link the engine can dispatch across without the
 * meter in between: a solar array or fueled generator wired into a battery or
 * an EV, or a battery integrated into an EV's charger. The engine supports
 * exactly these pairs (grene-dt-sim-v2, devices/constants.py):
 *
 *   generator (solar | fueled) <-> storage (battery | ev)
 *   battery                    <-> ev
 *
 * Each link is stored on BOTH devices in the sysconfig, under the key that
 * device kind uses for that partner kind:
 *
 *   solar / generator : coupled_bidirectional_device_ids  -> batteries, EVs
 *   battery           : coupled_generator_device_ids      -> solars, generators
 *                       coupled_bidirectional_device_ids  -> EVs
 *   ev                : coupled_generator_device_ids      -> solars, generators
 *                       coupled_storage_device_ids        -> batteries
 *
 * The engine unions the two sides, so a link written on either is one
 * coupling; the mutations in deviceMutations.js write both so the JSON never
 * has to be read twice to know who is coupled to whom. Pure module: no React,
 * no HTTP, no sysconfig access - deviceSelectors.js and deviceMutations.js
 * apply these rules to a sysconfig.
 *
 * A coupling and a strategy are different things. The coupling says the wire
 * exists; the strategy says whether the optimizer is bound to use it. Only
 * the strategies in COUPLING_ENFORCEMENT pin a link; for every other strategy
 * the engine reports the coupling in the results (routing attribution, the
 * '<Battery>-To <EV> (kWh)' column) without constraining the dispatch.
 * couplingEnforcement() tells the form which case it is looking at so it can
 * say so - it never changes a strategy on its own.
 *
 * @typedef {"battery"|"ev"|"solar"|"generator"} CouplableType
 */

/**
 * For each device type, the sysconfig keys it stores couplings under and the
 * partner types each key holds.
 * @type {Record<CouplableType, Array<{ key: string, partners: CouplableType[] }>>}
 */
export const COUPLING_LINKS = Object.freeze({
  solar: [
    { key: "coupled_bidirectional_device_ids", partners: ["battery", "ev"] },
  ],
  generator: [
    { key: "coupled_bidirectional_device_ids", partners: ["battery", "ev"] },
  ],
  battery: [
    { key: "coupled_generator_device_ids", partners: ["solar", "generator"] },
    { key: "coupled_bidirectional_device_ids", partners: ["ev"] },
  ],
  ev: [
    { key: "coupled_generator_device_ids", partners: ["solar", "generator"] },
    { key: "coupled_storage_device_ids", partners: ["battery"] },
  ],
});

/** Every key any device may store a coupling under. */
export const ALL_COUPLING_KEYS = Object.freeze(
  Array.from(
    new Set(
      Object.values(COUPLING_LINKS).flatMap((links) => links.map((l) => l.key)),
    ),
  ),
);

/**
 * @param {string} type
 * @returns {boolean} whether devices of this type can be coupled at all
 */
export function isCouplableType(type) {
  return Object.prototype.hasOwnProperty.call(COUPLING_LINKS, type);
}

/**
 * The device types a device of `type` may be coupled to.
 * @param {string} type
 * @returns {CouplableType[]}
 */
export function couplablePartnerTypes(type) {
  if (!isCouplableType(type)) return [];
  return COUPLING_LINKS[type].flatMap((link) => link.partners);
}

/**
 * @param {string} typeA
 * @param {string} typeB
 * @returns {boolean} whether the engine supports coupling these two kinds
 */
export function isCouplablePair(typeA, typeB) {
  return couplablePartnerTypes(typeA).includes(typeB);
}

/**
 * The sysconfig key a device of `type` stores a partner of `partnerType`
 * under, or null when the pair is not couplable.
 * @param {string} type
 * @param {string} partnerType
 * @returns {string|null}
 */
export function couplingKeyFor(type, partnerType) {
  if (!isCouplableType(type)) return null;
  const link = COUPLING_LINKS[type].find((l) => l.partners.includes(partnerType));
  return link ? link.key : null;
}

/**
 * Map the modal's form discriminators to a couplable device type.
 * @param {{ device_type?: string, storage_mode?: string, production_type?: string }} formValues
 * @returns {CouplableType|null}
 */
export function formValuesToDeviceType(formValues) {
  if (!formValues) return null;
  if (formValues.device_type === "Storage") {
    if (formValues.storage_mode === "EV") return "ev";
    if (formValues.storage_mode === "BESS") return "battery";
    return null;
  }
  if (formValues.device_type === "Production") {
    if (formValues.production_type === "Fuel generator") return "generator";
    if (formValues.production_type === "Solar PV") return "solar";
    return null;
  }
  return null;
}

/**
 * Human labels for the types, for the picker and the table line.
 * @type {Record<CouplableType, string>}
 */
export const TYPE_LABELS = Object.freeze({
  battery: "Battery",
  ev: "EV",
  solar: "Solar",
  generator: "Generator",
});

/**
 * Which strategy on which side pins a coupling in the optimizer, per
 * (device type, partner type). `field` names the strategy field on the
 * device being edited when the enforcement lives there; `partnerSide` is set
 * when it lives on the partner instead, so the form can say so but has no
 * button to press. `values` are the strategies that enforce; `suggest` is
 * the one the button offers.
 */
const COUPLING_ENFORCEMENT = Object.freeze({
  "battery|ev": {
    field: "discharging_strategy",
    values: ["coupled_ev_only"],
    suggest: "coupled_ev_only",
    partnerLabel: "EV",
  },
  "battery|solar": {
    field: "charging_strategy",
    values: ["generator_only", "grid_for_self_loads_only"],
    suggest: "generator_only",
    partnerLabel: "generator",
  },
  "battery|generator": {
    field: "charging_strategy",
    values: ["generator_only", "grid_for_self_loads_only"],
    suggest: "generator_only",
    partnerLabel: "generator",
  },
  "ev|solar": {
    field: "charging_strategy",
    values: ["generator_only"],
    suggest: "generator_only",
    partnerLabel: "generator",
  },
  "ev|generator": {
    field: "charging_strategy",
    values: ["generator_only"],
    suggest: "generator_only",
    partnerLabel: "generator",
  },
  "ev|battery": {
    partnerSide: "the battery's discharging strategy is coupled_ev_only",
    partnerLabel: "battery",
  },
  "solar|battery": {
    partnerSide:
      "the battery's charging strategy is generator_only or grid_for_self_loads_only",
    partnerLabel: "battery",
  },
  "solar|ev": {
    partnerSide: "the EV's charging strategy is generator_only",
    partnerLabel: "EV",
  },
  "generator|battery": {
    partnerSide:
      "the battery's charging strategy is generator_only or grid_for_self_loads_only",
    partnerLabel: "battery",
  },
  "generator|ev": {
    partnerSide: "the EV's charging strategy is generator_only",
    partnerLabel: "EV",
  },
});

/**
 * Describe, for the device being edited, whether each group of selected
 * partners is pinned by a strategy the optimizer enforces.
 *
 * @param {CouplableType} type - the device being edited
 * @param {{ charging_strategy?: string, discharging_strategy?: string }} strategies
 * @param {Array<{ type: CouplableType }>} partners - the selected partners
 * @returns {Array<{
 *   partnerType: CouplableType,
 *   enforced: boolean,
 *   message: string,
 *   suggestion: null | { field: string, value: string, label: string },
 * }>} one entry per partner type present, unenforced ones first
 */
export function couplingEnforcement(type, strategies, partners) {
  const partnerTypes = Array.from(new Set((partners || []).map((p) => p.type)));
  const notes = [];

  partnerTypes.forEach((partnerType) => {
    const rule = COUPLING_ENFORCEMENT[`${type}|${partnerType}`];
    if (!rule) return;

    if (rule.partnerSide) {
      notes.push({
        partnerType,
        enforced: false,
        message:
          `Coupling to a ${rule.partnerLabel} is reported in the results ` +
          `and is only enforced by the optimizer when ${rule.partnerSide}. ` +
          `Set that on the ${rule.partnerLabel}'s form.`,
        suggestion: null,
      });
      return;
    }

    const current = strategies?.[rule.field];
    const enforced = rule.values.includes(current);
    notes.push({
      partnerType,
      enforced,
      message: enforced
        ? `The optimizer enforces the ${rule.partnerLabel} coupling ` +
          `(${rule.field.replace("_", " ")} is ${current}).`
        : `Coupling to a ${rule.partnerLabel} is reported in the results but ` +
          `not enforced by the optimizer unless ${rule.field.replace("_", " ")} ` +
          `is ${rule.values.join(" or ")}.`,
      suggestion: enforced
        ? null
        : { field: rule.field, value: rule.suggest, label: `Use ${rule.suggest}` },
    });
  });

  return notes.sort((a, b) => Number(a.enforced) - Number(b.enforced));
}
